import BoxGrid from '@/main/components/shared/boxGrid/boxGrid';
import { KOGTableRow } from '@/main/components/shared/kogBoxGrid/styles';
import { TextEllipsis } from '@/main/components/shared/styles';
import {
  getDefaultMessageResultsFound,
  getTranslateByKey
} from '@/main/services/utils';
import { TableCell } from '@mui/material';
import * as S from './styles';
import * as StylesShared from '@/main/components/shared/styles';
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';
import ResultNotFoundComponent from '@/main/components/shared/resultNotFound/resultNotFoundComponent';
import { useEffect, useState } from 'react';
import { systemGuideApi } from '@/main/api';
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';
import { SystemGuideSearchGridResponse } from '@/main/models/response/systemGuideResponse';
import { SystemGuideSearchRequest } from '@/main/models/request/systemGuideRequest';
import { isEmpty, isNil } from 'lodash';
import { downloadPdfFile } from '@/main/services/utilsFile';

export interface ISystemGuideGridData {
  isLoading: boolean;
  totalItems: number;
  data: SystemGuideSearchGridResponse[];
}

export const INITIAL_SYSTEM_GUIDE_GRID_STATE: ISystemGuideGridData = {
  isLoading: false,
  totalItems: 0,
  data: []
};

interface ISystemGuideSearchGridProps {
  gridData: ISystemGuideGridData;
  setGridData: React.Dispatch<React.SetStateAction<ISystemGuideGridData>>;
  formFilters: any;
  setSystemGuideSelected: React.Dispatch<
    React.SetStateAction<SystemGuideSearchGridResponse | undefined>
  >;
}

const SystemGuideSearchGrid: React.FC<ISystemGuideSearchGridProps> = ({
  gridData,
  setGridData,
  formFilters,
  setSystemGuideSelected
}) => {
  const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState(false);
  const [hasShowMore, setHasShowMore] = useState(false);
  const [pageIndex, setPageIndex] = useState(PaginationEnum.PageIndexIntial);
  const [loadingDownloadFile, setLoadingDownloadFile] = useState(false);

  useEffect(() => {
    setHasShowMore(gridData.data.length < gridData.totalItems);
  }, [gridData.data, gridData.totalItems]);

  useEffect(() => {
    setPageIndex(PaginationEnum.PageIndexIntial);
  }, [formFilters]);

  const titleGrid = getDefaultMessageResultsFound(
    gridData.data.length,
    gridData.totalItems
  );

  const rowHeaders = [
    getTranslateByKey('information.fileName'),
    getTranslateByKey('information.insurer'),
    getTranslateByKey('information.whoCanSee'),
    getTranslateByKey('information.language'),
    getTranslateByKey('information.userName'),
    <StylesShared.RowHeaderTextCenter>
      {getTranslateByKey('information.status')}
    </StylesShared.RowHeaderTextCenter>
  ];

  function renderStatus({ active }: SystemGuideSearchGridResponse) {
    const bgColor = active ? '#00cc66' : '#ff0000';
    const statusName = active
      ? getTranslateByKey('statusRegister.active')
      : getTranslateByKey('information.inactive');

    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{
          backgroundColor: bgColor,
          height: 30,
          width: 100,
          borderRadius: 5,
          paddingLeft: '20px',
          paddingRight: '20px',
          color: '#fff',
          fontWeight: 500
        }}>
        {statusName}
      </div>
    );
  }

  function onLoadInfiniteScroll(request: SystemGuideSearchRequest) {
    setIsLoadingInfiniteScroll(true);

    const requestGrid: SystemGuideSearchRequest = {
      ...request,
      pageIndex: request.pageIndex ?? PaginationEnum.PageIndexIntial,
      pageSize: request.pageSize ?? PaginationEnum.PageSize30
    };

    systemGuideApi
      .getSearchGrid(requestGrid)
      .then(response => {
        let resultData: SystemGuideSearchGridResponse[] = [];
        let resultTotalItems: number = 0;
        if (response.data) {
          resultData = response.data.results;
          resultTotalItems = response.data.totalNumberOfRecords;
        }

        const newData = resultData.filter(
          (item: any) =>
            !gridData.data.some((existing: any) => existing.guid === item.guid)
        );
        const updatedData = [...gridData.data, ...newData];
        setGridData({
          isLoading: false,
          totalItems: resultTotalItems,
          data: updatedData
        });
      })
      .finally(() => setIsLoadingInfiniteScroll(false));
  }

  function onLoadSearchGrid() {
    setPageIndex(prev => prev + 1);
    const formFiltersOnLoadSearchGrid = formFilters;
    if (formFiltersOnLoadSearchGrid) {
      const updatedFormFilters = {
        ...formFiltersOnLoadSearchGrid,
        pageIndex: pageIndex + 1
      };
      onLoadInfiniteScroll(updatedFormFilters);
    }
  }

  function handleRowBoxGrid(
    item: SystemGuideSearchGridResponse,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) {
    event.stopPropagation();
    setSystemGuideSelected(item);
  }

  function downloadSystemGuideFile(
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>,
    { guidFileImport, fileImportName }: SystemGuideSearchGridResponse
  ) {
    event.stopPropagation();

    if (loadingDownloadFile || isEmpty(guidFileImport) || isNil(guidFileImport)) return;

    setLoadingDownloadFile(true);
    systemGuideApi
      .downloadImportFile(guidFileImport)
      .then(response => {
        const base64 = response.data.result;
        downloadPdfFile(base64, fileImportName);
      })
      .finally(() => setLoadingDownloadFile(false));
  }

  if (gridData.isLoading) return <LoadingComponent />;
  if (gridData.data.length === 0 && formFilters === null) return null;
  if (gridData.data.length === 0 && formFilters !== null)
    return <ResultNotFoundComponent styles={{ marginTop: 10 }} />;
  if (gridData.data.length === 0 && !gridData.isLoading) return null;

  return (
    <>
      <S.Container>
        <BoxGrid
          title={titleGrid}
          rowHeaders={rowHeaders}
          infiniteScrollConfig={{
            dataLength: gridData.data.length,
            fetchData: () => onLoadSearchGrid(),
            hasMore: hasShowMore,
            isLoading: isLoadingInfiniteScroll
          }}>
          {gridData.data.map(item => (
            <KOGTableRow
              key={item.guidSystemGuide}
              id={`id-searchAnnouncement-tableRow-result-${item.guidSystemGuide}`}
              onClick={e => handleRowBoxGrid(item, e)}>
              <TableCell>
                <TextEllipsis title={item.fileImportName}>
                  <S.LinkText
                    $isDisabled={loadingDownloadFile}
                    onClick={e => downloadSystemGuideFile(e, item)}>
                    {item.fileImportName}
                  </S.LinkText>
                </TextEllipsis>
              </TableCell>

              <TableCell>
                <TextEllipsis title={item.insurerName}>
                  {item.insurerName}
                </TextEllipsis>
              </TableCell>

              <TableCell>
                <TextEllipsis title={item.companyTypeName}>
                  {item.companyTypeName}
                </TextEllipsis>
              </TableCell>

              <TableCell>
                <TextEllipsis title={item.cultureName}>
                  {item.cultureName}
                </TextEllipsis>
              </TableCell>

              <TableCell>
                <TextEllipsis title={item.userName}>
                  {item.userName}
                </TextEllipsis>
              </TableCell>

              <TableCell>
                <S.AreaStatus>{renderStatus(item)}</S.AreaStatus>
              </TableCell>
            </KOGTableRow>
          ))}
        </BoxGrid>
      </S.Container>
    </>
  );
};

export default SystemGuideSearchGrid;
